import { useMemo, type ReactElement } from 'react';

import { useChatStore } from '../ChatProvider';
import { useAutoScroll } from '../hooks/useAutoScroll';
import type { Message } from '../state/types';
import { MessageBubble } from './MessageBubble';

export function MessageList(): ReactElement {
  const messages = useChatStore((s) => s.messages);

  const visibleMessages = useMemo(() => stripSeededWelcome(messages), [messages]);

  const lastSig =
    visibleMessages.length > 0
      ? `${visibleMessages.length}:${visibleMessages.at(-1)?.text?.length ?? 0}`
      : '0';
  const { scrollRef, bottomRef } = useAutoScroll(lastSig);

  const lastActionableBotId = useMemo(
    () => findLastActionableBotId(visibleMessages),
    [visibleMessages],
  );

  return (
    <div
      ref={scrollRef}
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-label="Histórico da conversa"
      className="from-primary/[0.04] to-background flex-1 overflow-y-auto bg-gradient-to-b"
      data-slot="message-list"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-7 px-5 py-7">
        {visibleMessages.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            isLastActionableBot={m.id === lastActionableBotId}
          />
        ))}
        <div ref={bottomRef} aria-hidden className="h-px w-full shrink-0" />
      </div>
    </div>
  );
}

function stripSeededWelcome(messages: Message[]): Message[] {
  const first = messages[0];
  if (first?.kind === 'text' && first.sender === 'bot' && first.hideActions === true) {
    return messages.slice(1);
  }
  return messages;
}

function findLastActionableBotId(messages: Message[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (
      m?.kind === 'text' &&
      m.sender === 'bot' &&
      m.hideActions !== true &&
      m.isStreaming !== true &&
      m.isTyping !== true &&
      m.text.length > 0
    ) {
      return m.id;
    }
  }
  return null;
}
